import { QueryClient, defaultShouldDehydrateQuery } from '@tanstack/react-query';
import { persistQueryClient } from '@tanstack/react-query-persist-client';
import { createSyncStoragePersister } from '@tanstack/query-sync-storage-persister';

const VITE_FLAG_SESSION_STALE_TIME_IN_HOURS = import.meta.env.VITE_FLAG_SESSION_STALE_TIME_IN_HOURS || 4;

// localStorage key the persister reads/writes — exported so the 401 interceptor can clear the
// exact same entry before a hard reload.
export const PERSIST_KEY = 'REACT_QUERY_OFFLINE_CACHE';

// Bump this to discard already-persisted caches on users' next load (e.g. pre-fix caches that
// persisted the session identity, or a stale `subscriptions` snapshot that stranded active
// subscribers on the paywall).
const PERSIST_BUSTER = 'auth-no-persist-v2';

// Never persist the session identity, the auth-derived user profile, or subscription entitlement:
// these must be revalidated against the server on every load, not restored from localStorage.
// Persisting auth/user let a stale/expired session masquerade as authenticated and caused a
// /login ⇄ /dashboard redirect loop. Persisting `subscriptions` served a pre-activation snapshot
// as fresh for hours, keeping paying users stuck behind the paywall.
const NON_PERSISTED_QUERY_KEYS = ['auth', 'user', 'subscriptions'];

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: VITE_FLAG_SESSION_STALE_TIME_IN_HOURS * 60 * 60 * 1000,
      refetchOnMount: false,
      refetchOnReconnect: false,
      refetchOnWindowFocus: false,
    },
  },
});

const localStoragePersister = createSyncStoragePersister({
  storage: window.localStorage,
  key: PERSIST_KEY,
});

persistQueryClient({
  queryClient,
  persister: localStoragePersister,
  maxAge: VITE_FLAG_SESSION_STALE_TIME_IN_HOURS * 60 * 60 * 1000,
  buster: PERSIST_BUSTER,
  dehydrateOptions: {
    shouldDehydrateQuery: (query) =>
      !NON_PERSISTED_QUERY_KEYS.includes(query.queryKey[0] as string) &&
      defaultShouldDehydrateQuery(query),
  },
});

export default queryClient;
